/**
 * Reports Navigation Utility Functions
 *
 * Handles the logic for organizing and building navigation items for reports
 * in the side navigation. Follows Single Responsibility Principle.
 */

import { SideNavigationProps } from '@amzn/awsui-components-core';
import { ReportsConfiguration } from 'src/types/admin/reportsConfig.types';
import {
  ASIN_ESSENTIALS_REPORT_NAME,
  ASIN_ESSENTIALS_REPORT_ROUTE,
} from 'src/components/reports/asin-essentials-report/asinEssentialsReportConstants';

// ============================================================================
// Types
// ============================================================================

type ReportGroup = Record<string, ReportsConfiguration[]>;

type SectionGroupItem =
  | SideNavigationProps.Link
  | SideNavigationProps.Section
  | SideNavigationProps.LinkGroup
  | SideNavigationProps.ExpandableLinkGroup;

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Groups reports by their report_group_name field.
 * Reports without a group name are assigned to 'UNGROUPED'.
 *
 * @param reports - Array of reports to group
 * @returns Object mapping group names to arrays of reports
 */
export const groupReportsByName = (reports: ReportsConfiguration[]): ReportGroup => {
  return reports.reduce((acc, report) => {
    const groupName = report.report_group_name || 'UNGROUPED';
    if (!acc[groupName]) {
      acc[groupName] = [];
    }
    acc[groupName].push(report);
    return acc;
  }, {} as ReportGroup);
};

/**
 * Builds navigation items for ungrouped reports (flattened, no section).
 * Reports are sorted by report_unique_code in ascending order.
 *
 * @param reports - Array of ungrouped reports
 * @returns Array of link items sorted by report_unique_code
 */
export const buildUngroupedReportItems = (reports: ReportsConfiguration[]): SideNavigationProps.Link[] => {
  return reports
    .sort((a, b) => a.report_unique_code.localeCompare(b.report_unique_code))
    .map((report) => ({
      type: 'link' as const,
      text: report.report_name,
      href: `/reports/${report.report_unique_code}`,
    }));
};

/**
 * Builds navigation sections for grouped reports.
 * Reports within each group are sorted by report_unique_code in ascending order.
 * Group names are also sorted alphabetically.
 * All grouped sections are collapsed by default.
 *
 * @param groupedReports - Object mapping group names to report arrays
 * @returns Array of section items with sorted reports
 */
export const buildGroupedReportSections = (groupedReports: ReportGroup): SideNavigationProps.Section[] => {
  return Object.keys(groupedReports)
    .filter((groupName) => groupName !== 'UNGROUPED')
    .sort()
    .map((groupName) => ({
      type: 'section' as const,
      text: groupName,
      defaultExpanded: false,
      items: groupedReports[groupName]
        .sort((a, b) => a.report_unique_code.localeCompare(b.report_unique_code))
        .map((report) => ({
          type: 'link' as const,
          text: report.report_name,
          href: `/reports/${report.report_unique_code}`,
        })),
    }));
};

/**
 * Builds the complete Reports section with all reports organized by group.
 *
 * Organization strategy:
 * 1. UNGROUPED reports appear first, flattened at top level, sorted by report_unique_code
 * 2. Grouped reports appear as expandable link groups, alphabetically sorted by group name
 * 3. Reports within each group are sorted by report_unique_code
 *
 * @param reports - Array of accessible reports
 * @returns Section item for Reports (collapsible, consistent with other nav sections)
 */
/**
 * Static report entries that are not backed by a ReportConfig record.
 *
 * The ASIN Essentials Report loads its data from a hardcoded (per-stage) S3
 * location and is served by a dedicated static route, so it has no backend
 * config record and must be injected into the nav list directly.
 */
const buildStaticReportItems = (): SideNavigationProps.Link[] => [
  {
    type: 'link',
    text: ASIN_ESSENTIALS_REPORT_NAME,
    href: ASIN_ESSENTIALS_REPORT_ROUTE,
  },
];

export const buildReportsSectionGroup = (reports: ReportsConfiguration[]): SideNavigationProps.Section => {
  const staticReportItems = buildStaticReportItems();

  if (reports.length === 0) {
    return {
      type: 'section',
      text: 'Reports',
      items: staticReportItems,
    };
  }

  const groupedReports = groupReportsByName(reports);
  const reportsItems: SectionGroupItem[] = [];

  // Add UNGROUPED reports first (flattened at top level, sorted)
  if (groupedReports['UNGROUPED']) {
    reportsItems.push(...buildUngroupedReportItems(groupedReports['UNGROUPED']));
  }

  // Add grouped reports as expandable link groups (sorted)
  const groupedSections = buildGroupedReportSections(groupedReports);
  for (const section of groupedSections) {
    reportsItems.push({
      type: 'expandable-link-group',
      text: section.text,
      href: '',
      defaultExpanded: false,
      items: section.items,
    });
  }

  // Static (non-config) reports appear after the config-driven entries.
  reportsItems.push(...staticReportItems);

  return {
    type: 'section',
    text: 'Reports',
    items: reportsItems,
  };
};
